@import '../../styles/palette.scss';
@import '../../styles/fonts.scss';
@import '../../styles/deviceBreakpoints.scss';
@import '../../styles/screenReader.scss';
@import '../input/mixins.scss';

$top-cell-border: 0.175rem;

.tableCell {
  height: 3.5rem;
  padding: 0 0.5rem;
  position: relative;
  vertical-align: middle;
  width: auto;
}

.tableWrapper {
  margin-top: 1em;

  @mixin shouldBreak() {
    overflow: auto;

    table,
    thead,
    tbody,
    th,
    tr,
    td {
      display: block;
    }

    thead tr {
      @include screenReaderOnly();
    }

    tbody tr td:last-child {
      border-bottom: none !important;
    }

    table tbody tr {
      border: $top-cell-border solid _palette(table-top-row-border) !important;
      border-radius: 5px;
      margin-left: 0.25em;
      margin-right: 0.25em;

      &:not(:last-child) {
        margin-bottom: 0.25em;
      }
    }

    td {
      display: table-row;
      height: auto;
      word-break: break-word;
      max-height: 3em;

      &:before {
        content: attr(data-th) ':';
        display: table-cell;
        font-weight: bold;
        padding: 0.5em;
        word-break: keep-all;
      }

      button {
        margin-bottom: 0.25em;
      }
    }
  }

  @include target(mobile) {
    @include shouldBreak;
  }

  // Need manual overflow to handle overflowing table children, such as dropdown menus, not being considered as overflowing
  &.overflow {
    overflow-y: auto;
  }

  .table {
    @include barlow(16);
    height: 100%;
    table-layout: auto;
    width: 100%;

    .tableHeader {
      text-align: left;

      .tableRow {
        &.noActiveHeaders {
          & .tableCell:not(.active) {
            color: _palette(text);
          }
        }

        .tableCell {
          @include barlowMedium(16);
          background-color: _palette(bg-primary);
          height: auto;
          padding: 0.5rem 0.5rem 0.65rem;
          position: sticky;
          top: 0;
          vertical-align: inherit;
          color: _palette(text);
          z-index: 100;

          &:after {
            content: '';
            position: absolute;
            left: 0;
            bottom: 0;
            width: 100%;
            border-bottom: 0.15rem solid _palette(table-top-row-border);
          }

          &:focus {
            &:after {
              border-bottom: $top-cell-border solid _palette(secondary);
            }

            outline: none;

            .searchFieldWrapper {
              &.icon {
                .searchIcon {
                  fill: _palette(text);
                }
              }
            }
          }

          &.clickable {
            padding-left: 1rem;

            &.active {
              &:after {
                border-bottom: $top-cell-border solid _palette(secondary);
              }
            }

            &:hover {
              &:after {
                border-bottom: $top-cell-border solid _palette(secondary);
              }

              .searchFieldWrapper {
                &.icon {
                  .searchIcon {
                    fill: _palette(text);
                  }
                }
              }
            }
          }

          .sortArrows {
            position: absolute;
            width: 100%;
            height: 100%;
            border: none;
            background: none;
            top: 0;
            left: 0;
            cursor: pointer;

            .arrowWrapper {
              position: absolute;
              bottom: 1.1em;
              right: 0.25em;
              display: flex;
              flex-direction: column;
              height: 1.5em;
              width: 1em;

              .arrow {
                height: 0.5em;
                width: 0.5em;

                svg {
                  fill: _palette(text-secondary);
                }

                &.active {
                  svg {
                    fill: _palette(secondary);
                  }
                }
              }
            }
          }

          .searchFieldWrapper {
            position: relative;
            z-index: 100;

            &.icon {
              .container {
                padding-left: 1.5rem;
              }
            }

            &.activeIcon {
              .searchIcon {
                fill: _palette(text);
              }
            }

            &.inactiveIcon {
              .searchIcon {
                fill: _palette(text-secondary);
              }
            }

            .container {
              height: auto;
            }

            .inputLabel {
              margin-top: 0;
            }

            .input {
              @include barlowMedium(16);
              background-color: transparent !important;
              padding: 0;
              position: relative;
              height: auto;
              border: none;

              &::-webkit-input-placeholder {
                color: _palette(text) !important;
              }

              &:-ms-input-placeholder {
                color: _palette(text) !important;
              }

              &::placeholder {
                color: _palette(text) !important;
                opacity: 1;
              }

              &::placeholder {
                color: _palette(text) !important;
              }

              &:focus {
                &::-webkit-input-placeholder {
                  color: _palette(text-secondary) !important;
                }

                &:-ms-input-placeholder {
                  color: _palette(text-secondary) !important;
                }

                &::placeholder {
                  color: _palette(text-secondary) !important;
                }
              }

              &:hover,
              &:focus {
                box-shadow: none;
              }
            }

            .searchIcon {
              position: absolute;
            }
          }
        }
      }
    }

    .tableMain {
      .tableRow {
        &:not(.alert):not(.sky):not(.shade):not(.blank):not(.deactivated) {
          &:nth-child(odd) {
            background-color: _palette(bg-primary-light);
          }

          &:nth-child(even) {
            background-color: _palette(bg-primary);
          }
        }

        &.alert {
          background-color: _palette(table-row-alert);
        }

        &.sky {
          background-color: _palette(table-row-sky);
        }

        &.shade {
          background-color: _palette(table-row-shade);
        }

        &.deactivated {
          background-color: _palette(table-row-deactivated);
        }

        &.blank {
          opacity: 0;
        }

        &:not(.top) {
          & * {
            a {
              color: _palette(text);
            }
          }
        }

        &:not(.top):hover {
          color: _palette(secondary);

          &:not(.sky):not(.shade):not(.alert):not(.deactivated) {
            background-color: _palette(bg-secondary);
          }

          & * {
            a {
              color: _palette(secondary);
            }
          }
        }

        .tableCell:not(.blank) {
          border-bottom: 1px solid _palette(table-row-border);
        }
      }
    }
  }

  &.breakTable {
    @include shouldBreak;
  }
}
